// Permissions utility to check user permissions for various modules

// Define permission types
export type Permission = 'view' | 'create' | 'edit' | 'delete' | 'export' | 'View' | 'Create' | 'Edit' | 'Delete' | 'Export';

export interface ModulePermission {
  id: number;
  name: string;
  permissions: Permission[];
}

export type ModulePermissions = Record<string, ModulePermission[]>;

// Function to get module permissions from localStorage
export const getModulePermissions = (): ModulePermissions | null => {
  const permissions = typeof window !== 'undefined' ? window.localStorage.getItem('modules') : null;
  if (!permissions) {
    return null;
  }
  
  try {
    return JSON.parse(permissions);
  } catch (error) {
    console.error('Failed to parse module permissions', error);
    return null;
  }
};

// Function to check if user has specific permission for a module
export const hasPermission = (moduleName: string, permission: Permission): boolean => {
  const modulePermissions = getModulePermissions();
  if (!modulePermissions) {
    // Redirect to logout if no permissions
    window.location.href = '/logout';
    return false;
  }
  
  // Iterate through business IDs (keys in modulePermissions)
  for (const businessId in modulePermissions) {
    const modules = modulePermissions[businessId];
    
    // Find the module by name (case-insensitive)
    const module = modules.find(m => 
      m.name.toLowerCase() === moduleName.toLowerCase()
    );
    
    if (module) {
      // Check if the module has the specified permission (case-insensitive)
      return module.permissions.some(p => 
        p.toLowerCase() === permission.toLowerCase()
      );
    }
  }
  
  return false;
};

// Function to check if user has view permission for a module
export const canView = (moduleName: string): boolean => {
  return hasPermission(moduleName, 'view');
};

// Function to check if user has create permission for a module
export const canCreate = (moduleName: string): boolean => {
  return hasPermission(moduleName, 'create');
};

// Function to check if user has edit permission for a module
export const canEdit = (moduleName: string): boolean => {
  return hasPermission(moduleName, 'edit');
};

// Function to check if user has delete permission for a module
export const canDelete = (moduleName: string): boolean => {
  return hasPermission(moduleName, 'delete');
};

// Function to check if user has export permission for a module
export const canExport = (moduleName: string): boolean => {
  return hasPermission(moduleName, 'export');
};

// Function to validate permissions and redirect to logout if not found
export const validatePermissions = (): boolean => {
  const permissions = getModulePermissions();
  if (!permissions) {
    window.location.href = '/logout';
    return false;
  }
  return true;
};

// Function to initialize missing modules with default permissions
export const initializeMissingModules = (): void => {
  const modulePermissions = getModulePermissions();
  if (!modulePermissions) return;

  const newModules = ['Inventory', 'Manufacturing', 'Procurement', 'Warehouse'];
  let updated = false;

  // Check each business in modulePermissions
  for (const businessId in modulePermissions) {
    const modules = modulePermissions[businessId];
    
    // Check if any new modules are missing
    newModules.forEach(moduleName => {
      const moduleExists = modules.find(m => 
        m.name.toLowerCase() === moduleName.toLowerCase()
      );
      
      if (!moduleExists) {
        // Add the missing module with full permissions
        const moduleId = MODULE_MAP[moduleName as keyof typeof MODULE_MAP];
        modules.push({
          id: moduleId,
          name: moduleName,
          permissions: ['view', 'create', 'edit', 'delete', 'export']
        });
        updated = true;
      }
    });
  }

  // Save updated permissions back to localStorage
  if (updated) {
    localStorage.setItem('modules', JSON.stringify(modulePermissions));
  }
};

// Map of module names to their corresponding IDs for reference
export const MODULE_MAP = {
  Accounting: 1,
  Sales: 2,
  CRM: 3,
  Dashboard: 4,
  Users: 5,
  Customers: 6,
  Products: 7,
  Quotations: 8,
  Invoices: 9,
  Reports: 10,
  Settings: 11,
  Bills: 12,
  Receipts: 13,
  Deals: 14,
  Campaigns: 15,
  Workflows: 16,
  Settlements: 17,
  'Social Media': 18,
  Business: 19,
  Suppliers: 20,
  Calendar: 21,
  Tasks: 22,
  Notes: 23,
  Files: 24,
  Documents: 25,
  Contacts: 26,
  Reminders: 27,
  Events: 28,
  Inventory: 29,
  Manufacturing: 30,
  Procurement: 31,
  Warehouse: 32
}; 